import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Pencil } from 'lucide-react';
import { useClients, useCreateClient, useUpdateClient, useClient } from '../hooks/useClients.js';
import Table from '../components/Table.jsx';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';
import PageHeader from '../components/PageHeader.jsx';

const statusVariant = { active: 'success', suspended: 'warning', disabled: 'danger' };

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function TokenExpiry({ expiresAt }) {
  if (!expiresAt) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const days = daysUntil(expiresAt);
  const color = days <= 7 ? 'text-red-400' : days <= 30 ? 'text-amber-400' : 'text-emerald-400';
  return (
    <span
      className={`font-mono text-xs cursor-default ${color}`}
      title={days >= 0 ? `Vence en ${days} día${days !== 1 ? 's' : ''}` : `Venció hace ${Math.abs(days)} día${Math.abs(days) !== 1 ? 's' : ''}`}
    >
      {fmtDate(expiresAt)}
    </span>
  );
}

function TokenStatus({ credIsActive, authType }) {
  if (credIsActive === null || credIsActive === undefined)
    return <Badge variant="default">sin token</Badge>;
  if (!credIsActive)
    return <Badge variant="danger">revocado</Badge>;
  return <Badge variant="success">{authType === 'oauth' ? 'OAuth' : 'Static'}</Badge>;
}

function parseApiError(e, fallback = 'Error desconocido') {
  const data = e?.response?.data;
  if (!data) return fallback;
  if (Array.isArray(data.error)) return data.error.map(err => err.message).join(' · ');
  if (typeof data.error === 'string') return data.error;
  return data.message || fallback;
}

const EMPTY_FORM = {
  code: '', businessName: '', mpUserId: '', taxId: '',
  email: '', phone: '', description: '', externalWebhookUrl: '',
};

/* ── Shared form fields ───────────────────────────────────── */
function ClientForm({ form, onChange, isEdit = false }) {
  const f = (k) => (e) => onChange(k, e.target.value);
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {!isEdit && (
          <div className="col-span-2">
            <Input
              label="Código *"
              value={form.code}
              onChange={(e) => onChange('code', e.target.value.toUpperCase())}
              placeholder="Ej: MCANET"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Identificador único, sin espacios. No se puede modificar después.
            </p>
          </div>
        )}
        <Input label="Razón social *"    value={form.businessName}      onChange={f('businessName')}      placeholder="Farmacia SRL" />
        <Input label="CUIT"              value={form.taxId}             onChange={f('taxId')}             placeholder="30-12345678-9" />
        <Input label="Email"             value={form.email}             onChange={f('email')}             type="email" placeholder="admin@empresa.com" />
        <Input label="Teléfono"          value={form.phone}             onChange={f('phone')}             placeholder="+54 9 11 1234-5678" />
        <Input label="MP User ID"         value={form.mpUserId}          onChange={f('mpUserId')}          placeholder="Se completa al conectar OAuth" />
        <Input label="Webhook URL"       value={form.externalWebhookUrl} onChange={f('externalWebhookUrl')} placeholder="http://servidor/mp-webhook" />
      </div>
      <Input label="Descripción"         value={form.description}       onChange={f('description')} />
    </>
  );
}

/* ── Edit Modal ───────────────────────────────────────────── */
function EditModal({ clientId, onClose }) {
  const { data: client, isLoading } = useClient(clientId);
  const updateMutation = useUpdateClient(clientId);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);

  // Initialize form once client data is loaded
  if (client && !form) {
    setForm({
      businessName:        client.business_name        || '',
      mpUserId:            client.mp_user_id           || '',
      taxId:               client.tax_id               || '',
      email:               client.email                || '',
      phone:               client.phone                || '',
      description:         client.description          || '',
      externalWebhookUrl:  client.external_webhook_url || '',
    });
  }

  const handleSave = () => {
    setErr(null);
    updateMutation.mutate(form, {
      onSuccess: () => onClose(),
      onError: (e) => setErr(parseApiError(e, 'Error al guardar')),
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Editar cliente — ${client?.code || '…'}`}
      maxWidth="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button loading={updateMutation.isPending} onClick={handleSave} disabled={!form}>Guardar</Button>
        </>
      }
    >
      {isLoading || !form ? (
        <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Cargando…</div>
      ) : (
        <>
          <ClientForm form={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} isEdit />
          {err && <p className="text-sm text-red-400">{err}</p>}
        </>
      )}
    </Modal>
  );
}

/* ── Main Page ────────────────────────────────────────────── */
export default function Clients() {
  const navigate = useNavigate();
  const { data, isLoading } = useClients();
  const createMutation = useCreateClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [createError, setCreateError] = useState(null);

  const handleCreate = () => {
    setCreateError(null);
    createMutation.mutate(form, {
      onSuccess: () => {
        setCreateOpen(false);
        setForm(EMPTY_FORM);
      },
      onError: (e) => setCreateError(parseApiError(e, 'Error al crear cliente')),
    });
  };

  const columns = [
    { key: 'code',          header: 'Código',    render: (r) => <span className="font-mono text-sm">{r.code}</span> },
    { key: 'business_name', header: 'Empresa' },
    { key: 'tax_id',        header: 'CUIT',      render: (r) => r.tax_id  || <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { key: 'email',         header: 'Email',     render: (r) => r.email   || <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { key: 'mp_user_id',    header: 'MP User ID',render: (r) => <span className="font-mono text-xs">{r.mp_user_id}</span> },
    {
      key: 'status', header: 'Cliente',
      render: (r) => <Badge variant={statusVariant[r.status] || 'default'}>{r.status}</Badge>,
    },
    {
      key: 'cred_token_hint', header: 'Token',
      render: (r) => r.cred_token_hint
        ? <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>{r.cred_token_hint}</span>
        : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      key: 'cred_is_active', header: 'Token estado',
      render: (r) => <TokenStatus credIsActive={r.cred_is_active} authType={r.cred_auth_type} />,
    },
    {
      key: 'cred_expires_at', header: 'Vencimiento',
      render: (r) => <TokenExpiry expiresAt={r.cred_expires_at} />,
    },
    {
      key: 'actions', header: '',
      render: (r) => (
        <button
          onClick={(e) => { e.stopPropagation(); setEditId(r.id); }}
          className="p-1.5 rounded-lg hover:bg-bg-elevated transition-colors"
          style={{ color: 'var(--text-muted)' }}
          title="Editar cliente"
        >
          <Pencil size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader icon={Building2} title="Clientes" subtitle={data ? `${data.length} registrados` : undefined} color="violet">
        <Button onClick={() => setCreateOpen(true)}>+ Nuevo cliente</Button>
      </PageHeader>

      <Table
        columns={columns}
        data={data}
        loading={isLoading}
        empty="No hay clientes registrados"
        onRowClick={(row) => navigate(`/clients/${row.id}`)}
      />

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nuevo cliente"
        maxWidth="max-w-2xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button loading={createMutation.isPending} onClick={handleCreate}>Crear</Button>
          </>
        }
      >
        <ClientForm form={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} />
        {createError && <p className="text-sm text-red-400">{createError}</p>}
      </Modal>

      {/* Edit modal */}
      {editId && <EditModal clientId={editId} onClose={() => setEditId(null)} />}
    </div>
  );
}
